import {Component} from 'react';
import {WithTranslation, withTranslation} from 'react-i18next';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheckSquare} from "@fortawesome/pro-regular-svg-icons/faCheckSquare";
import {faSquare} from "@fortawesome/pro-regular-svg-icons/faSquare";
import {
    MileageBillingCustomReportParameters,
    MileageBillingScheduledReportParameters,
    ReportPeriodType,
    ScheduledReportPeriod,
} from '../../../../../../store/reports';
import {FunctionalLocationsSelectionsControlValue} from '../../../../../analytics/dashboard-controls/functional-locations-selector/types/functional-locations-selections-control-value';
import {FunctionalLocationSelection} from '../../../../../../store/analytics';
import ScheduledPeriodSelectorComponent from '../../components/scheduled-period-selector.component';
import LocationSelectorComponent from '../../../../components/location-selector.component';
import styles from './mileage-billing.module.scss';
import {FleetCustomerWithConfiguration} from '../../../../../../store/fleet-customers';
import DateRangeSelectorComponent from '../../../../components/date-range-selector.component';
import ToggleDisplay from "../../../../../../utils/toggle-display";

class MileageBillingComponent extends Component<AllProps, AllState> {

    constructor(props) {
        super(props);
        const {parameters} = this.props;

        let period: ScheduledReportPeriod = ScheduledReportPeriod.PAST_MONTH;
        let locations: FunctionalLocationSelection[] = [];
        let mountDemountOnly = false;
        if (parameters && parameters.locations) {
            locations = parameters.locations;
        }
        if (parameters && parameters.period) {
            period = parameters.period;
        }
        if (parameters && parameters.mountDemountOnly) {
            mountDemountOnly = parameters.mountDemountOnly;
        }

        this.state = {
            period,
            mountDemountOnly,
            functionalLocationSelection: new FunctionalLocationsSelectionsControlValue('', locations),
        };
    }

    public render(): JSX.Element {
        const {periodType, fleetCustomer, t} = this.props;
        const {period, functionalLocationSelection, mountDemountOnly} = this.state;

        return (
            <div className={styles.content}>
                {periodType === ReportPeriodType.Scheduled
                    ? <>
                        <div className={styles.title}>{t('Period')}</div>
                        <ScheduledPeriodSelectorComponent
                            defaultPeriod={period}
                            onPeriodChange={(p) => this.onPeriodChanged(p)}
                        />
                        <div className={styles.title}>{t('Parameters')}</div>
                    </>
                    : null
                }
                <div className={styles.form}>
                    <div className={styles.formRow}>
                        <div className={styles.info}>{t('Select a customer')}</div>
                        <LocationSelectorComponent
                            defaultValue={functionalLocationSelection && functionalLocationSelection.value.length > 0 ? functionalLocationSelection : undefined}
                            onFunctionalLocationChange={(controlValue) => this.onFunctionalLocationChanged(controlValue)}/>
                    </div>
                    {periodType === ReportPeriodType.Custom
                        ? <div className={styles.formRow}>
                            <div className={styles.formColumn}>
                                <div className={styles.info}>{t('Specify a period')}</div>
                                <DateRangeSelectorComponent
                                    earliestDate={fleetCustomer?.earliestContractStartDate}
                                    latestDate={new Date()}
                                    fromDateChanged={(date => this.onFromDateChanged(date))}
                                    toDateChanged={(date => this.onToDateChanged(date))}
                                />
                            </div>
                        </div>
                        : null
                    }
                    <div className={styles.formRow}>
                        <div className={styles.info}>{t('Optional Filter')}</div>
                        <div className={styles.select}>
                            <label onClick={() => this.onMountDemountOnlyChanged()}>
                                <div className={styles.checkBox}>
                                    <ToggleDisplay if={mountDemountOnly}>
                                        <FontAwesomeIcon icon={faCheckSquare}/>
                                    </ToggleDisplay>
                                    <ToggleDisplay if={!mountDemountOnly}>
                                        <FontAwesomeIcon icon={faSquare}/>
                                    </ToggleDisplay>
                                </div>
                                <div className={styles.checkBoxText}>
                                    {t('Only include mounted / demounted tires')}
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private onFunctionalLocationChanged(controlValue: FunctionalLocationsSelectionsControlValue): void {
        const {periodType, onCustomParametersChanged, onScheduledParametersChanged} = this.props;
        const {functionalLocationSelection, period, fromDate, toDate, mountDemountOnly} = this.state;

        if (!functionalLocationSelection || (functionalLocationSelection && !controlValue.equals(functionalLocationSelection))) {
            if (periodType === ReportPeriodType.Custom) {
                onCustomParametersChanged({
                    functionalLocationSelection: controlValue,
                    fromDate,
                    toDate,
                    mountDemountOnly,
                });
            }
            if (periodType === ReportPeriodType.Scheduled) {
                onScheduledParametersChanged({
                    period,
                    locations: controlValue.value,
                    mountDemountOnly,
                });
            }

            this.setState({
                functionalLocationSelection: controlValue,
            });
        }
    }

    private onPeriodChanged(period: ScheduledReportPeriod) {
        const {onScheduledParametersChanged} = this.props;
        const {functionalLocationSelection, mountDemountOnly} = this.state;

        onScheduledParametersChanged({
            period,
            locations: functionalLocationSelection!.value,
            mountDemountOnly,
        });

        this.setState({
            period,
        });
    }

    private onFromDateChanged(fromDate?: Date) {
        const {onCustomParametersChanged} = this.props;

        this.setState({
            fromDate,
        }, () => {
            // Use callback and fresh state because from & to date might change simultaneously
            const {functionalLocationSelection, toDate, mountDemountOnly} = this.state;

            onCustomParametersChanged({
                functionalLocationSelection,
                fromDate,
                toDate,
                mountDemountOnly,
            });
        });
    }

    private onToDateChanged(toDate?: Date) {
        const {onCustomParametersChanged} = this.props;

        this.setState({
            toDate,
        }, () => {
            // Use callback and fresh state because from & to date might change simultaneously
            const {functionalLocationSelection, fromDate, mountDemountOnly} = this.state;

            onCustomParametersChanged({
                functionalLocationSelection,
                fromDate,
                toDate,
                mountDemountOnly,
            });
        });
    }

    private onMountDemountOnlyChanged(): void {
        const {periodType, onCustomParametersChanged, onScheduledParametersChanged} = this.props;
        const {functionalLocationSelection, period, fromDate, toDate, mountDemountOnly} = this.state;

        if (periodType === ReportPeriodType.Custom) {
            onCustomParametersChanged({
                functionalLocationSelection,
                fromDate,
                toDate,
                mountDemountOnly: !mountDemountOnly,
            });
        }
        if (periodType === ReportPeriodType.Scheduled) {
            onScheduledParametersChanged({
                period,
                locations: functionalLocationSelection!.value,
                mountDemountOnly: !mountDemountOnly,
            });
        }

        this.setState({
            mountDemountOnly: !mountDemountOnly,
        });
    }
}

export default withTranslation()(MileageBillingComponent);

interface OwnProps {
    periodType: ReportPeriodType;
    fleetCustomer?: FleetCustomerWithConfiguration;
    parameters?: MileageBillingScheduledReportParameters;
    onCustomParametersChanged: (parameters: MileageBillingCustomReportParameters) => void;
    onScheduledParametersChanged: (parameters: MileageBillingScheduledReportParameters) => void;
}

type AllProps = WithTranslation & OwnProps;

interface OwnState {
    period: ScheduledReportPeriod;
    functionalLocationSelection?: FunctionalLocationsSelectionsControlValue;
    fromDate?: Date;
    toDate?: Date;
    mountDemountOnly: boolean;
}

type AllState = OwnState;
